// Types/Interfaces
interface CountryPattern {
  pattern: RegExp;
  code: string;
}

// Constants
const COMMON_INVALID_VALUES = [
  'undefined',
  'unknown',
  'none',
  'null',
  '<nil>',
  'nil',
  'n/a',
  'na',
  '',
];

const COUNTRY_PATTERNS: CountryPattern[] = [
  // United States variations
  {
    pattern: /\b(?:the\s+)?united\s+states(?:\s+of\s+america)?\b/,
    code: 'US',
  },
  { pattern: /\b(?:the\s+)?u\.?s\.?(?:\s*a\.?)?\b/, code: 'US' },

  // United Kingdom variations
  { pattern: /\b(?:the\s+)?united\s+kingdom\b/, code: 'GB' },
  { pattern: /\b(?:great\s+)?britain\b/, code: 'GB' },
  { pattern: /\b(?:the\s+)?u\.?k\.?\b/, code: 'GB' },
  { pattern: /\bengland\b/, code: 'GB' },

  // Canada
  { pattern: /\bcanada\b/, code: 'CA' },

  // Germany
  { pattern: /\bgermany\b/, code: 'DE' },
  { pattern: /\bdeutschland\b/, code: 'DE' },

  // France
  { pattern: /\bfrance\b/, code: 'FR' },

  // China variations
  { pattern: /\b(?:people's\s+republic\s+of\s+)?china\b/, code: 'CN' },
  { pattern: /\bp\.?r\.?c\.?\b/, code: 'CN' },

  // Russia variations
  { pattern: /\b(?:russian\s+federation|russia)\b/, code: 'RU' },

  // Korea variations
  { pattern: /\b(?:south\s+)?korea\b/, code: 'KR' },
  { pattern: /\brepublic\s+of\s+korea\b/, code: 'KR' },

  // Other major countries
  { pattern: /\bjapan\b/, code: 'JP' },
  { pattern: /\baustralia\b/, code: 'AU' },
  { pattern: /\bbrazil\b/, code: 'BR' },
  { pattern: /\bindia\b/, code: 'IN' },
  { pattern: /\bitaly\b/, code: 'IT' },
  { pattern: /\bspain\b/, code: 'ES' },
  { pattern: /\b(?:netherlands|holland)\b/, code: 'NL' },
  { pattern: /\bsweden\b/, code: 'SE' },
  { pattern: /\bnorway\b/, code: 'NO' },
  { pattern: /\bdenmark\b/, code: 'DK' },
  { pattern: /\bfinland\b/, code: 'FI' },
  { pattern: /\bpoland\b/, code: 'PL' },
  { pattern: /\bireland\b/, code: 'IE' },
  { pattern: /\bsingapore\b/, code: 'SG' },
  { pattern: /\bswitzerland\b/, code: 'CH' },
  { pattern: /\baustria\b/, code: 'AT' },
  { pattern: /\bbelgium\b/, code: 'BE' },
  { pattern: /\bczech\s+republic\b/, code: 'CZ' },
  { pattern: /\bportugal\b/, code: 'PT' },
  { pattern: /\bgreece\b/, code: 'GR' },
  { pattern: /\bhungary\b/, code: 'HU' },
  { pattern: /\bromania\b/, code: 'RO' },
  { pattern: /\bbulgaria\b/, code: 'BG' },
  { pattern: /\bcroatia\b/, code: 'HR' },
  { pattern: /\bslovakia\b/, code: 'SK' },
  { pattern: /\bslovenia\b/, code: 'SI' },
  { pattern: /\bluxembourg\b/, code: 'LU' },
  { pattern: /\bnew\s+zealand\b/, code: 'NZ' },
  { pattern: /\bsouth\s+africa\b/, code: 'ZA' },
  { pattern: /\bisrael\b/, code: 'IL' },
  { pattern: /\bturkey\b/, code: 'TR' },
  { pattern: /\bmexico\b/, code: 'MX' },
  { pattern: /\bargentina\b/, code: 'AR' },
  { pattern: /\bchile\b/, code: 'CL' },
  { pattern: /\bcolombia\b/, code: 'CO' },
  { pattern: /\bperu\b/, code: 'PE' },
  { pattern: /\bvenezuela\b/, code: 'VE' },
  { pattern: /\bukraine\b/, code: 'UA' },
  { pattern: /\bthailand\b/, code: 'TH' },
  { pattern: /\bmalaysia\b/, code: 'MY' },
  { pattern: /\bindonesia\b/, code: 'ID' },
  { pattern: /\bphilippines\b/, code: 'PH' },
  { pattern: /\btaiwan\b/, code: 'TW' },
  { pattern: /\bhong\s+kong\b/, code: 'HK' },
  { pattern: /\bvietnam\b/, code: 'VN' },
  { pattern: /\begypt\b/, code: 'EG' },
  { pattern: /\bsaudi\s+arabia\b/, code: 'SA' },
  { pattern: /\b(?:united\s+arab\s+emirates|u\.?a\.?e\.?)\b/, code: 'AE' },
];

// Internal utility functions
const isValueInList = (value: any, invalidList: string[]): boolean => {
  if (!value || typeof value !== 'string') {
    return value === '' || value === null || value === undefined;
  }
  const normalizedValue = value.toString().toLowerCase().trim();
  return invalidList.some(invalid => normalizedValue === invalid.toLowerCase());
};

// Exported functions
export const isValidBasicValue = (statistic: any): boolean => {
  return !isValueInList(statistic.value, COMMON_INVALID_VALUES);
};

export const isValidPortValue = (statistic: any): boolean => {
  // First run basic validation to filter out common invalid values
  if (!isValidBasicValue(statistic)) {
    return false;
  }

  // Then run port-specific validation: must be numeric and in valid range
  const value = statistic?.value?.toString().trim();
  return (
    value &&
    !isNaN(parseInt(value)) &&
    parseInt(value) >= 0 &&
    parseInt(value) <= 65535
  );
};

export const normalizeCountryValue = (countryValue: string): string => {
  if (!countryValue || typeof countryValue !== 'string') {
    return countryValue;
  }

  const trimmed = countryValue.trim();

  // If already a 2-letter code (uppercase), return as-is
  if (/^[A-Z]{2}$/.test(trimmed)) {
    return trimmed;
  }

  // If already a 2-letter code (lowercase), convert to uppercase
  if (/^[a-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  // Convert to lowercase for pattern matching
  const normalizedValue = trimmed.toLowerCase();

  // Try pattern matching for flexible country recognition
  for (const { pattern, code } of COUNTRY_PATTERNS) {
    if (pattern.test(normalizedValue)) {
      return code;
    }
  }

  // If no pattern matches, return original value
  return trimmed;
};

export const normalizeOriginName = (value: string): string => {
  switch (value) {
    case 'qualys':
      return 'Qualys';
    case 'rapid7':
      return 'Rapid7';
    case 'gcp':
      return 'GCP';
    case 'repository':
      return 'Repository';
    case 'github':
      return 'GitHub';
    case 'tenablevm':
      return 'Tenable VM';
    case 'amazon':
      return 'AWS';
    default:
      // Capitalize first letter of each word
      return value
        .split(/[-_\s]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
};

/**
 * Normalizes AS Name values by removing everything after the first space
 * This truncates verbose AS names to their primary identifier
 *
 * @param asname - AS Name string to normalize
 * @returns Normalized AS Name with content after first space removed
 *
 * @example
 * normalizeAsnameValue('AMAZON-02 - Amazon.com, Inc.') // 'AMAZON-02'
 * normalizeAsnameValue('GOOGLE') // 'GOOGLE'
 * normalizeAsnameValue('CLOUDFLARE CloudFlare, Inc.') // 'CLOUDFLARE'
 */
export const normalizeAsnameValue = (asname: string): string => {
  if (!asname || typeof asname !== 'string') {
    return asname;
  }

  const trimmed = asname.trim();

  // Find first space and truncate everything after it
  const firstSpaceIndex = trimmed.indexOf(' ');

  if (firstSpaceIndex === -1) {
    // No space found, return as-is
    return trimmed;
  }

  // Return everything before the first space
  return trimmed.substring(0, firstSpaceIndex);
};

/**
 * Validates and normalizes email values for display
 * Filters out invalid email formats (URLs, malformed strings, etc.)
 *
 * RFC 5322 compliant email validation
 * Supports: user+tag@example.com, user.name@sub.example.co.uk
 * Rejects: URLs, malformed addresses, missing @ or domain
 *
 * @param email - Email string to validate and normalize
 * @returns Normalized email (trimmed, lowercase) or null if invalid
 *
 * @example
 * normalizeEmailValue('User@Example.COM  ') // 'user@example.com'
 * normalizeEmailValue('https://example.com/contact') // null (invalid)
 * normalizeEmailValue('user+tag@example.com') // 'user+tag@example.com' (valid)
 */
export const normalizeEmailValue = (
  email: string | null | undefined
): string | null => {
  if (!email || typeof email !== 'string') return null;

  const trimmed = email.trim().toLowerCase();

  // Filter out URLs (http://, https://)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return null;
  }

  // RFC 5322 compliant email regex (simplified but comprehensive)
  // Supports: +, -, _, ., alphanumeric in local part
  // Supports: subdomains, international TLDs
  const emailRegex =
    /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

  if (!emailRegex.test(trimmed)) {
    return null;
  }

  return trimmed;
};

/**
 * Helper function to check if a value is a valid email
 *
 * @param email - Email string to check
 * @returns True if valid email, false otherwise
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  return normalizeEmailValue(email) !== null;
};
